import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { cacheDaAplicacao } from "@/api/cache";
import { ProvedorDaSessao } from "@/sessao/contexto";
import { RotaProtegida } from "@/sessao/RotaProtegida";
import { Chassi } from "@/chassi/Chassi";
import { ProvedorDeAvisos } from "@/componentes/Avisos";
import { VisaoGeral } from "@/telas/VisaoGeral";
import { EmBreve } from "@/telas/EmBreve";
import { Boletos } from "@/telas/boletos/Boletos";
import { BoletoDaRota } from "@/telas/boletos/DetalheDoBoleto";
import { Operacao } from "@/telas/operacao/Operacao";
import { AbaDeCedentes, CedenteDaAplicacao } from "@/telas/sistemas/AbaDeCedentes";
import {
  AbaPadraoDaAplicacao,
  AcessoDaAplicacao,
  ChavesDaAplicacao,
  ConsumoDaAplicacao,
  ContratoDaAplicacao,
  DetalheDoSistema,
  EntregaDaAplicacao,
} from "@/telas/sistemas/DetalheDoSistema";
import { Sistemas } from "@/telas/sistemas/Sistemas";
import { AbaDeAplicacoes } from "@/telas/cedentes/AbaDeAplicacoes";
import { AbaDeBoletos } from "@/telas/cedentes/AbaDeBoletos";
import { AbaDeContas } from "@/telas/cedentes/AbaDeContas";
import { AbaDeDados } from "@/telas/cedentes/AbaDeDados";
import { AbaPadrao } from "@/telas/cedentes/AbaPadrao";
import { Cedentes } from "@/telas/cedentes/Cedentes";
import { ConvenioPorId } from "@/telas/cedentes/ConvenioPorId";
import { DetalheDoConvenio } from "@/telas/cedentes/DetalheDoConvenio";
import { DetalheDoVinculo } from "@/telas/cedentes/DetalheDoVinculo";
import { PaginaDoCedente } from "@/telas/cedentes/PaginaDoCedente";
import { RedirecionarBeneficiarios } from "@/telas/cedentes/RedirecionarBeneficiarios";
import { PlanoDaRota } from "@/telas/planos/DetalheDoPlano";
import { Planos } from "@/telas/planos/Planos";
import { FaturaDaRota } from "@/telas/faturas/DetalheDaFatura";
import { Faturas } from "@/telas/faturas/Faturas";

export function App() {
  return (
    <QueryClientProvider client={cacheDaAplicacao}>
      <ProvedorDeAvisos>
      <ProvedorDaSessao>
        <BrowserRouter>
          <RotaProtegida>
            <Routes>
              <Route element={<Chassi />}>
                <Route index element={<VisaoGeral />} />

                {/*
                  Detalhe de boleto, fatura e plano é painel sobre a lista (design D7):
                  rota filha, com a lista montada embaixo, na mesma rolagem. Fechar é
                  navegar para ela, com os filtros, sem remontar nada.
                */}
                <Route path="boletos" element={<Boletos />}>
                  <Route path=":id" element={<BoletoDaRota />} />
                </Route>
                <Route path="operacao" element={<Operacao />} />

                <Route path="aplicacoes" element={<Sistemas />} />
                {/*
                  A aplicação é página, e a aba é segmento, com entrada no histórico.
                  Aba fora destas seis não casa com rota nenhuma e cai em `*`. O cedente
                  é painel sobre a aba, como o vínculo do lado do cedente.
                */}
                <Route path="aplicacoes/:id" element={<DetalheDoSistema />}>
                  <Route index element={<AbaPadraoDaAplicacao />} />
                  <Route path="contrato" element={<ContratoDaAplicacao />} />
                  <Route element={<AbaDeCedentes />}>
                    <Route path="cedentes" element={null} />
                    <Route path="cedentes/:pessoaId" element={<CedenteDaAplicacao />} />
                  </Route>
                  <Route path="chaves" element={<ChavesDaAplicacao />} />
                  <Route path="acesso" element={<AcessoDaAplicacao />} />
                  <Route path="entrega" element={<EntregaDaAplicacao />} />
                  <Route path="consumo" element={<ConsumoDaAplicacao />} />
                </Route>
                {/*
                  O caminho antigo continua abrindo a tela: link salvo e
                  histórico do navegador de quem já operava antes do rótulo
                  "Aplicações". `replace` para o voltar não cair num laço.
                */}
                <Route path="sistemas" element={<Navigate to="/aplicacoes" replace />} />
                <Route path="planos" element={<Planos />}>
                  <Route path=":id" element={<PlanoDaRota />} />
                </Route>
                <Route path="faturas" element={<Faturas />}>
                  <Route path=":id" element={<FaturaDaRota />} />
                </Route>

                {/*
                  Cedente é página, e o painel lateral é camada sobre a aba (design
                  D1). As rotas de layout, sem caminho, mantêm a aba montada embaixo do
                  painel, com a mesma rolagem: fechar o painel é navegar para `contas`
                  ou `aplicacoes`, sem remontar a lista.
                */}
                <Route path="cedentes" element={<Cedentes />} />
                <Route path="cedentes/:id" element={<PaginaDoCedente />}>
                  <Route index element={<AbaPadrao />} />
                  <Route path="dados" element={<AbaDeDados />} />
                  <Route element={<AbaDeContas />}>
                    <Route path="contas" element={null} />
                    <Route path="convenios/:convenioId" element={<DetalheDoConvenio />} />
                  </Route>
                  <Route element={<AbaDeAplicacoes />}>
                    <Route path="aplicacoes" element={null} />
                    <Route path="aplicacoes/:sistemaId" element={<DetalheDoVinculo />} />
                  </Route>
                  <Route path="boletos" element={<AbaDeBoletos />} />
                </Route>

                {/* Os dois caminhos antigos, resolvidos com `replace`. */}
                <Route path="beneficiarios" element={<RedirecionarBeneficiarios />} />
                <Route path="convenios/:id" element={<ConvenioPorId />} />

                <Route
                  path="configuracoes"
                  element={
                    <EmBreve
                      titulo="Configurações"
                      texto="Em breve."
                      dica="Operadores, webhook do provider e dados do ambiente. Falta a rota na API administrativa: hoje o operador sai do subcomando api operador criar, e o webhook, da configuração."
                    />
                  }
                />

                {/*
                  Rota desconhecida NÃO redireciona para a raiz em silêncio: um
                  link errado que abre a visão geral esconde o link errado. A
                  tela diz o que aconteceu e a barra lateral continua ali.
                */}
                <Route
                  path="*"
                  element={
                    <EmBreve
                      titulo="Endereço não encontrado"
                      texto="Esta rota não existe."
                    />
                  }
                />
              </Route>
            </Routes>
          </RotaProtegida>
        </BrowserRouter>
      </ProvedorDaSessao>
      </ProvedorDeAvisos>
    </QueryClientProvider>
  );
}
